import React, { type FC, useContext, useEffect, useRef } from 'react';
import { Route, Router as BrowserRouter, Switch } from 'react-router-dom';

import config from 'config';

import history from 'utils/history';

import Home from 'pages';
import { useMyRoleQuery } from 'hooks/query/useMyRole';
import { useUserInfoQuery } from 'hooks/query/useUserInfo';
import { handleError } from 'utils/errorHandler';
import { UserContext } from './App';

import { HOME } from '../constants/routes';

// Top level application router.
const Router: FC = () => {
  const { isMaintenanceMode } = config;

  const user = useContext(UserContext);
  const userId = typeof user === 'string' ? undefined : Number(user.id);
  const {
    data: userRoles = [],
    isLoading: isUserRoleLoading,
    isError: isUserRoleError,
    error: userRoleError,
    errorUpdatedAt: userRoleErrorUpdatedAt,
  } = useMyRoleQuery(userId, {
    enabled: !isMaintenanceMode,
  });
  const {
    isError: isUserInfoError,
    error: userInfoError,
    errorUpdatedAt: userInfoErrorUpdatedAt,
  } = useUserInfoQuery({ enabled: !isMaintenanceMode });

  const lastHandledUserRoleErrorAtRef = useRef(0);
  const lastHandledUserInfoErrorAtRef = useRef(0);

  useEffect(() => {
    if (!isUserRoleError || !userRoleError || userRoleErrorUpdatedAt <= lastHandledUserRoleErrorAtRef.current) {
      return;
    }

    handleError(userRoleError);
    lastHandledUserRoleErrorAtRef.current = userRoleErrorUpdatedAt;
  }, [isUserRoleError, userRoleError, userRoleErrorUpdatedAt]);

  useEffect(() => {
    if (!isUserInfoError || !userInfoError || userInfoErrorUpdatedAt <= lastHandledUserInfoErrorAtRef.current) {
      return;
    }

    handleError(userInfoError);
    lastHandledUserInfoErrorAtRef.current = userInfoErrorUpdatedAt;
  }, [isUserInfoError, userInfoError, userInfoErrorUpdatedAt]);

  return (
    <BrowserRouter history={history}>
      <Switch>
        <Route path={HOME} render={() => <Home userRoles={userRoles} isUserRoleLoading={isUserRoleLoading} />} />
      </Switch>
    </BrowserRouter>
  );
};

export default Router;
